import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchStatsDataSet } from '@/store/slices/ownerHubStats';
import { isoDay } from '@/nova/pages/affiliates/reports-helpers';

import { campaignsByAsset } from './link-attribution';
import type { TrackingLink, TrackingLinkKind } from './tracking-links-api';
import { useBrandLinks } from './useBrandLinks';
import { CAMPAIGN_STATS_DATA_SET, endExclusive } from './useCampaignStats';
import { campaignGroups, matchedCampaignMetrics, sumMetricField } from './video-stats';

/** A glanceable per-asset stat for a library card (kept tiny on purpose). An entry exists for an asset ONLY when it
 *  has a tracking link (a landing page: any link landing on it); its absence means "no tracking link yet", which the
 *  card turns into a contextual hint instead of a row of zeros. The figures may legitimately be 0. */
export interface AssetMiniStats {
  registrations: number;
  ftds: number;
  deposits: number;
}

/** How far back a library's subtle stats look (recent performance, not lifetime). */
const WINDOW_DAYS = 90;

/**
 * One-shot, per-brand stats for a marketing library's GRID (Video Ads, Image Banners, Mini-games, HTML5 Banners,
 * Landing Pages): the brand's tracking links (`useBrandLinks`, cached and shared) plus a single grouped read of
 * `daily_affiliate_activity` aggregated on `campaign`, mapped to each listed asset by its links' `campaign` values
 * (`campaignsByAsset`). An asset gets an entry iff it HAS a link, even at zero activity.
 */
export interface AssetLibraryStats {
  /** Per-asset stats, keyed by asset id; an entry exists iff the asset has a tracking link (see AssetMiniStats). */
  byId: Record<string, AssetMiniStats>;
  /** The brand's links once read (null before the first read for this brand lands): a library that also counts
   *  links (a landing page card's "N ad links") reads them from here instead of reading again. */
  links: TrackingLink[] | null;
  /** Force a re-read of links + activity. Call it after an action that may MINT or move a link (the owner closed an
   *  asset's composer) so the grid reflects it without a full page refresh. */
  refresh: () => void;
  /** True once the links + activity read has SETTLED for the current brand. `byId` absence only means "no tracking
   *  link" once this is true; before it (first read in flight, or a first-load error that never settled) the caller
   *  must NOT infer "no link" and show the hint. Resets on a brand switch until the new brand's read lands. */
  loaded: boolean;
  /** The FIRST read for this brand failed (the links, or the activity) and nothing has settled since: the card keeps
   *  its slot empty (no skeleton left shimmering, no false "no link" hint). A later read that lands clears it. */
  failed: boolean;
}

export interface AssetLibraryStatsOptions {
  /** The library shows its grid (no asset composer open over it). Coming back to the grid re-reads links + activity:
   *  the composer may have minted or moved a link. Leave it out to refresh by hand (`refresh`). */
  onGrid?: boolean;
  /** The library's own list has LANDED for the CURRENT brand (its list hook's `loaded` and not `listFailed`). The list
   *  hooks hand out no ids before that (never the previous brand's), and a failed read settles `loaded` with none: so
   *  an empty id list then means "not read", not "no assets", and nothing settles on it. */
  idsLoaded: boolean;
}

export function useAssetLibraryStats(
  kind: TrackingLinkKind,
  ids: readonly string[],
  { onGrid, idsLoaded }: AssetLibraryStatsOptions,
): AssetLibraryStats {
  const dispatch = useAppDispatch();
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  const brand = useBrandLinks();
  const { reload: reloadLinks } = brand;
  const linksReady = Boolean(brand.tenantId) && !brand.loading && !(brand.error && brand.links.length === 0);

  // Each listed asset's campaigns, as a stable key: the effect re-runs when the mapping changes, not on every new
  // links array.
  const idsKey = ids.join('|');
  // The ids settle a "no link" answer only once they are this brand's list.
  const hasIds = ids.length > 0 && idsLoaded;
  const campaignsKey = React.useMemo(() => {
    if (!linksReady) return '';
    const map = campaignsByAsset(brand.links, kind, idsKey ? idsKey.split('|') : []);
    return JSON.stringify([...map.entries()].map(([id, set]) => [id, [...set].sort()]).sort());
  }, [linksReady, brand.links, kind, idsKey]);

  const [byId, setById] = React.useState<Record<string, AssetMiniStats>>({});
  // The brand (stats brand + creatives tenant) `byId` has SETTLED for: `loaded` derives from it so a brand switch
  // flips it back to false until the new brand's read lands. A transient error leaves it (and the last figures).
  const brandKey = casinoInstanceId && brand.tenantId ? `${casinoInstanceId}|${brand.tenantId}` : undefined;
  const [loadedFor, setLoadedFor] = React.useState<string | undefined>(undefined);
  const loaded = brandKey !== undefined && loadedFor === brandKey;
  // The brand a first activity read failed for (a links failure is read off useBrandLinks directly).
  const [failedFor, setFailedFor] = React.useState<string | undefined>(undefined);
  const linksFailed = Boolean(brand.tenantId) && !brand.loading && Boolean(brand.error) && brand.links.length === 0;
  const failed = !loaded && (linksFailed || (brandKey !== undefined && failedFor === brandKey));
  // Bumped by refresh() to re-read the activity when the mapping itself did not change.
  const [reloadKey, setReloadKey] = React.useState(0);

  // Syncs with the network: the grouped activity of the listed assets' links.
  React.useEffect(() => {
    if (!casinoInstanceId || !brandKey || !linksReady) return undefined;
    const entries = campaignsKey ? (JSON.parse(campaignsKey) as [string, string[]][]) : [];
    if (entries.length === 0) {
      setById({});
      // An empty list (still loading, or no asset yet), or ids still from the previous brand, settle nothing:
      // stamping them would let the assets listed next read as "no tracking link" while their stats are in flight.
      if (hasIds) setLoadedFor(brandKey);
      return undefined;
    }
    let stale = false;
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - WINDOW_DAYS);
    void dispatch(
      fetchStatsDataSet({
        casinoInstanceId,
        dataSet: CAMPAIGN_STATS_DATA_SET,
        startDate: isoDay(from),
        endDate: endExclusive(isoDay(today)),
        aggregateOn: ['campaign'],
      }),
    )
      .unwrap()
      .then((entry) => {
        if (stale) return;
        const groups = campaignGroups(entry.data);
        const out: Record<string, AssetMiniStats> = {};
        for (const [id, campaigns] of entries) {
          const metricsList = matchedCampaignMetrics(groups, new Set(campaigns));
          // The asset has a link, so it gets an entry even at zero activity: the card shows its real numbers.
          out[id] = {
            registrations: sumMetricField(metricsList, 'registrations'),
            ftds: sumMetricField(metricsList, 'ftds'),
            deposits: sumMetricField(metricsList, 'deposits'),
          };
        }
        setById(out);
        // Only now does a missing entry mean "no tracking link" for THIS brand.
        setLoadedFor(brandKey);
        setFailedFor(undefined);
      })
      .catch(() => {
        // Keep the last figures on a transient error rather than flickering them away, and leave loadedFor alone: a
        // FIRST-load error stays "not loaded" (no hint, not a false one), marked failed so no skeleton is left on.
        if (!stale) setFailedFor(brandKey);
      });
    return () => {
      stale = true;
    };
  }, [casinoInstanceId, brandKey, linksReady, campaignsKey, hasIds, dispatch, reloadKey]);

  const refresh = React.useCallback((): void => {
    reloadLinks();
    setReloadKey((k) => k + 1);
  }, [reloadLinks]);

  // Syncs with the network: back on the grid from a composer, read again (guarded on the transition, so once).
  const wasOnGridRef = React.useRef(onGrid);
  React.useEffect(() => {
    if (onGrid === true && wasOnGridRef.current === false) refresh();
    wasOnGridRef.current = onGrid;
  }, [onGrid, refresh]);
  return { byId, links: brand.tenantId && !brand.loading ? brand.links : null, refresh, loaded, failed };
}

export default useAssetLibraryStats;
